import React, { useState, useEffect, useCallback } from 'react';
import AdminVideoGallery from './AdminVideoGallery';
import { useAdminVideosAdapter } from '@/hooks/querry_hooks/admin/useAdminVideosQuery';
import { useAdminImagesAdapter } from '@/hooks/querry_hooks/admin/useAdminImagesQuery';
import { useVideoStatusCounts } from '@/hooks/querry_hooks/common/useVideoStatusCounts';
import { useImageStatusCounts } from '@/hooks/querry_hooks/common/useImageStatusCounts';
import { useWebSocket } from '@/utils/contexts/WebSocketContext';
import { useDeleteImage } from '@/hooks/querry_hooks/dashboard/useImageMutations';
import { useQueryClient } from '@tanstack/react-query';
import { ToastNotification } from '@/components/ui/common/ToastNotification';
import { createFavouriteSuccessCallback, createArchiveSuccessCallback } from '@/utils/common/toastCallbacks';

interface VideoGalleryWithFiltersProps {
  isAdmin: boolean;
  userEmail: string | null;
  pageValue?: 'Videos' | 'Images' | 'ExtensionRequests';
  setPageValue?: React.Dispatch<React.SetStateAction<'Videos' | 'Images' | 'ExtensionRequests'>>;
}

const VideoGalleryWithFilters: React.FC<VideoGalleryWithFiltersProps> = ({ 
  isAdmin, 
  userEmail, 
  pageValue = 'Videos', 
  setPageValue,
}) => {
  const [serverFilters, setServerFilters] = useState<{
    status?: string;
    user_email?: string;
    sort_by?: string;
    video_type?: string;
  }>({
    sort_by: 'recent', // Default to most recent
    video_type: 'all',
  });
  const [imageFilters, setImageFilters] = useState<{
    status?: string;
    user_email?: string;
    sort_by?: string;
    video_type?: string;
  }>({
    sort_by: 'recent', // Default to most recent
    video_type: 'all',
  });

  // Toast state for favourite and archive actions
  const [favouriteToastOpen, setFavouriteToastOpen] = useState(false);
  const [favouriteToastMessage, setFavouriteToastMessage] = useState('');
  const [archiveToastOpen, setArchiveToastOpen] = useState(false);
  const [archiveToastMessage, setArchiveToastMessage] = useState('');
  
  const requestLimit = 12;

  // Video hooks - NOW USING REACT QUERY! 🚀
  // Both admins and regular users can visit content-review page and see all videos
  const {
    videos,
    pinnedVideos,
    error: videosError,
    refetch: refetchVideos,
    isLoading: isLoadingVideos,
    // isLoadingPinnedVideos,
    isLoadingMore: isLoadingMoreVideos,
    hasMore: hasMoreVideos,
    totalCount: videoTotalCount,
    allUsers: videoUsers,
    isLoadingUsers: isLoadingVideoUsers,
    handleDownloadLastFrame,
    // NEW: Use optimistic functions directly from adapter (like dashboard does)
    // optimisticPinVideo,
    // optimisticUnpinVideo,
    optimisticDeleteVideo,
    loadMore: loadMoreVideos
  } = useAdminVideosAdapter({
    ...serverFilters,
    limit: requestLimit,
    isAdmin: true // Always fetch all videos for content-review page (for both admins and users)
  });

  // Image hooks - NOW USING REACT QUERY! 🚀
  // Both admins and regular users can visit content-review page and see all images
  const {
    images,
    error: imagesError,
    refetch: refetchImages,
    isLoading: isLoadingImages,
    isLoadingMore: isLoadingMoreImages,
    hasMore: hasMoreImages,
    totalCount: imageTotalCount,
    allUsers: imageUsers,
    isLoadingUsers: isLoadingImageUsers,
    optimisticDeleteImage,
    loadMore: loadMoreImages
  } = useAdminImagesAdapter({
    ...imageFilters,
    limit: requestLimit,
    isAdmin: pageValue === 'Images' // Only fetch when on Images tab
  });

  // Status counts hooks with current user email filter handling
  const { 
    counts: videoStatusCounts, 
    error: videoStatusCountsError 
  } = useVideoStatusCounts({ 
    userEmail: serverFilters.user_email === 'all' ? undefined : serverFilters.user_email 
  });
  
  const { 
    counts: imageStatusCounts, 
    error: imageStatusCountsError 
  } = useImageStatusCounts({ 
    userEmail: imageFilters.user_email === 'all' ? undefined : imageFilters.user_email 
  });
  
  // Image mutations with proper optimistic updates
  const deleteImageMutation = useDeleteImage();

  // Handle status count errors
  useEffect(() => {
    if (videoStatusCountsError) {
      console.error('Error fetching video status counts:', videoStatusCountsError);
    }
  }, [videoStatusCountsError]);

  useEffect(() => {
    if (imageStatusCountsError) {
      console.error('Error fetching image status counts:', imageStatusCountsError);
    }
  }, [imageStatusCountsError]);

  // Get current admin email
  useEffect(() => {
    // Admin email handling removed - not needed for React Query approach
  }, [userEmail]);

  const { subscribe, unsubscribe } = useWebSocket();
  const queryClient = useQueryClient();

  // WebSocket functions for pin/unpin notifications (same as dashboard)
  // const pinVideoWebSocket = (_videoId: string, _user_Email?: string) => {
  //   // WebSocket notification is now handled by React Query mutation's onSuccess
  //   // This is kept for compatibility with createPinHandler
  // };
  // const unpinVideoWebSocket = (_videoId: string, _user_Email?: string) => {
  //   // WebSocket notification is now handled by React Query mutation's onSuccess
  //   // This is kept for compatibility with createUnpinHandler
  // };

    // WebSocket integration for real-time pin/unpin updates
  useEffect(() => {
    if (isAdmin) {
      const handleWebSocketMessage = (data: any) => {
        
        // Handle video status updates
        if (data.event === 'video_status_updated') {
          
          // Invalidate admin videos and status counts
          queryClient.invalidateQueries({ 
            predicate: (query) => {
              return query.queryKey[0] === 'videos' && query.queryKey[1] === 'admin';
            }
          });
          queryClient.invalidateQueries({ 
            predicate: (query) => {
              return query.queryKey[0] === 'videos' && query.queryKey[1] === 'statusCounts';
            }
          });
        }
      };

      // Subscribe to WebSocket messages
      subscribe('admin-video-gallery', handleWebSocketMessage);

      // Cleanup subscription on unmount
      return () => {
        unsubscribe('admin-video-gallery');
      };
    }
  }, [isAdmin, subscribe, unsubscribe, queryClient]);

  // Helper function to get status counts based on page value
  const getStatusCounts = () => {
    if (pageValue === 'Videos') {
      return {
        approved: videoStatusCounts.approved,
        pending: videoStatusCounts.pending,
        rejected: videoStatusCounts.rejected,
        total: videoStatusCounts.total,
        approvedDuration: videoStatusCounts.approved_duration_minutes,
        pendingDuration: videoStatusCounts.pending_duration_minutes,
        rejectedDuration: videoStatusCounts.rejected_duration_minutes,
        totalDuration: videoStatusCounts.total_duration_minutes,
      };
    }
    
    return {
      approved: imageStatusCounts.approved,
      pending: imageStatusCounts.pending,
      rejected: imageStatusCounts.rejected,
      total: imageStatusCounts.total,
      approvedDuration: 0,
      pendingDuration: 0,
      rejectedDuration: 0,
      totalDuration: 0,
    };
  };

  // Pin/unpin handlers for admin interface - NOW USING OPTIMISTIC FUNCTIONS FROM ADAPTER
  // These are no longer needed - we use optimisticPinVideo/optimisticUnpinVideo directly from adapter
  // This matches the dashboard implementation pattern

  // Toast callback handlers
  const handleFavouriteSuccess = useCallback(
    createFavouriteSuccessCallback(setFavouriteToastMessage, setFavouriteToastOpen),
    []
  );

  const handleArchiveSuccess = useCallback(
    createArchiveSuccessCallback(setArchiveToastMessage, setArchiveToastOpen),
    []
  );

  // Delete handlers - React Query mutations handle optimistic updates
  const handleDeleteVideo = async (videoId: string) => {
    const currentVideo = videos.find((video: any) => video.id === videoId);
    const videoUserEmail = currentVideo?.user_email || '';
    optimisticDeleteVideo(videoId, videoUserEmail);
  };

  const handleDeleteImage = async (imageId: string) => {
    // Use the optimistic delete function for instant UI update
    optimisticDeleteImage(imageId);

    // Also call the mutation for the actual API request
    deleteImageMutation.mutate(imageId);
  };

  return (
    <>
      <AdminVideoGallery
        isAdmin={isAdmin}
        userEmail={userEmail}
        videos={videos}
        pinnedVideos={pinnedVideos}
        error={pageValue === 'Videos' ? videosError : imagesError}
        onRetry={pageValue === 'Videos' ? refetchVideos : refetchImages}
        onDeleteVideo={handleDeleteVideo}
      isLoading={pageValue === 'Videos' ? isLoadingVideos : isLoadingImages}
      // isLoadingPinnedVideos={isLoadingPinnedVideos}
      isLoadingMore={pageValue === 'Videos' ? isLoadingMoreVideos : isLoadingMoreImages}
      hasMore={pageValue === 'Videos' ? hasMoreVideos : hasMoreImages}
      allUsers={pageValue === 'Videos' ? videoUsers : imageUsers}
      isLoadingUsers={pageValue === 'Videos' ? isLoadingVideoUsers : isLoadingImageUsers}
      statusCounts={getStatusCounts()}
      totalCount={pageValue === 'Videos' ? videoTotalCount : imageTotalCount}
      handleDownloadLastFrame={handleDownloadLastFrame}
      // do optimisticPinVideo={optimisticPinVideo}
      // optimisticUnpinVideo={optimisticUnpinVideo}
      // pinVideoWebSocket={pinVideoWebSocket}
      // unpinVideoWebSocket={unpinVideoWebSocket}
      serverFilters={pageValue === 'Videos' ? serverFilters : imageFilters}
      setServerFilters={pageValue === 'Videos' ? setServerFilters : setImageFilters}
      loadMore={pageValue === 'Videos' ? loadMoreVideos : loadMoreImages}
      pageValue={pageValue}
      setPageValue={setPageValue}
      images={images}
      imagesError={imagesError}
      onRetryImages={refetchImages}
      onDeleteImage={handleDeleteImage}
      isLoadingImages={isLoadingImages}
      imagesTotalCount={imageTotalCount}
      onFavouriteSuccess={handleFavouriteSuccess}
      onArchiveSuccess={handleArchiveSuccess}
    />

    {/* Favourite Toast Notification */}
    <ToastNotification
      open={favouriteToastOpen}
      message={favouriteToastMessage}
      onClose={() => setFavouriteToastOpen(false)}
    />

    {/* Archive Toast Notification */}
    <ToastNotification
      open={archiveToastOpen}
      message={archiveToastMessage}
      onClose={() => setArchiveToastOpen(false)}
    />
    </>
  );
};

export default VideoGalleryWithFilters;
